import { createSelector } from 'reselect'
import type { RootState } from 'shared/store/reducers'
import { calculateFinalPriceWithPromotion } from 'shared/utils/ecommerce'
import { round } from 'shared/utils'
import type { Price, PurchasableItem } from './types'
import type { ClickAndCollectPromotion, DeliveryFees } from 'shared/types'
import { orderPickUpType } from 'shared/utils'

const { DELIVERY_IN_APP, TABLE_ORDER } = orderPickUpType

export const orderSelector = createSelector(
    (state: RootState) => state.ecommerceOrderState,
    order => order
)

export const pointsSelector = createSelector(
    (state: RootState) => state.ecommerceOrderState.products,
    (state: RootState) => state.ecommerceOrderState.promotions,
    (state: RootState) => state.ecommerceOrderState.deliveryFees.fees,
    (state: RootState) =>
        state.ecommerceOrderState.deliveryFees.feesPurchasable,

    (state: RootState) => state.ecommerceOrderState.collectMode,
    (state: RootState) => state.applicationState.pointsPerEuroClickAndCollect,
    (state: RootState) => state.applicationState.pointsPerEuroDeliveryInApp,
    (state: RootState) => state.applicationState.pointsPerEuroTableOrder,
    (
        products,
        promotions,
        fees,
        feesPurchasable,
        collectMode,
        pointsPerEuroClickAndCollect,
        pointsPerEuroDeliveryInApp,
        pointsPerEuroTableOrder
    ) => {
        let pointsPerEuroToUse = pointsPerEuroClickAndCollect
        if (collectMode === DELIVERY_IN_APP) {
            pointsPerEuroToUse = pointsPerEuroDeliveryInApp
        }
        if (collectMode === TABLE_ORDER) {
            pointsPerEuroToUse = pointsPerEuroTableOrder
        }

        return calculateCartPoints(
            products,
            pointsPerEuroToUse,
            promotions,
            fees,
            feesPurchasable
        )
    }
)

export const pointsPerEuroSelector = createSelector(
    (state: RootState) => state.ecommerceOrderState.collectMode,
    (state: RootState) => state.applicationState.pointsPerEuroClickAndCollect,
    (state: RootState) => state.applicationState.pointsPerEuroDeliveryInApp,
    (state: RootState) => state.applicationState.pointsPerEuroTableOrder,
    (
        collectMode,
        pointsPerEuroClickAndCollect,
        pointsPerEuroDeliveryInApp,
        pointsPerEuroTableOrder
    ) => {
        let pointsPerEuroToUse = pointsPerEuroClickAndCollect
        if (collectMode === DELIVERY_IN_APP) {
            pointsPerEuroToUse = pointsPerEuroDeliveryInApp
        }
        if (collectMode === TABLE_ORDER) {
            pointsPerEuroToUse = pointsPerEuroTableOrder
        }

        return pointsPerEuroToUse
    }
)

export const priceSelector = createSelector(
    (state: RootState) => state.ecommerceOrderState.products,
    (state: RootState) => state.ecommerceOrderState.promotions,
    (state: RootState) =>
        state.ecommerceOrderState.deliveryFees.feesPurchasable,
    (state: RootState) => state.ecommerceOrderState.deliveryFees.fees,
    (products, promotions, deliveryFeesPurchasable, deliveryFees) =>
        calculateCartPrice(
            products,
            promotions,
            deliveryFeesPurchasable,
            deliveryFees
        )
)

export const subTotalSelector = createSelector(
    (state: RootState) => state.ecommerceOrderState.products,
    products => sumProductFinalPrices(products)
)

const calculateCartPoints = (
    products: Array<PurchasableItem>,
    pointsPerEuro: number,
    promotions?: Array<ClickAndCollectPromotion>,
    deliveryFees?: DeliveryFees,
    deliveryFeesPurchasable?: PurchasableItem
): number => {
    const productsWithPoints = products.filter(
        product => product.earnPoints === true
    )
    const { finalPriceWithDeliveryFees } = calculateCartPrice(
        productsWithPoints,
        promotions,
        deliveryFeesPurchasable,
        deliveryFees
    )
    return Math.ceil(finalPriceWithDeliveryFees * pointsPerEuro)
}

const calculateCartPrice = (
    products: Array<PurchasableItem>,
    promotions?: Array<ClickAndCollectPromotion>,
    deliveryFeesPurchasable?: PurchasableItem,
    deliveryFees?: DeliveryFees
): Price & { finalPriceWithDeliveryFees: number } => {
    let { total, totalDiscounted } = products.reduce(
        (acc, product) => {
            const { available, quantity } = product
            if (available !== false) {
                acc.total += quantity * (product.originalPrice || 0)
                acc.totalDiscounted += quantity * (product.finalPrice || 0)
            }
            return acc
        },
        { total: 0, totalDiscounted: 0 }
    )

    if (promotions && promotions.length > 0) {
        promotions.forEach(promotion => {
            totalDiscounted = calculateFinalPriceWithPromotion(
                totalDiscounted,
                promotion,
                products
            )
        })
    }
    total = round(total, 2)
    totalDiscounted = round(totalDiscounted, 2)
    const freeDeliveryFees =
        deliveryFees && totalDiscounted >= deliveryFees.higherPrice

    return {
        originalPrice: total,
        finalPrice: totalDiscounted,
        finalPriceWithDeliveryFees:
            deliveryFeesPurchasable &&
            deliveryFeesPurchasable.finalPrice &&
            !freeDeliveryFees
                ? totalDiscounted + deliveryFeesPurchasable.finalPrice
                : totalDiscounted
    }
}

const sumProductFinalPrices = (products: Array<PurchasableItem>): number => {
    const total = products.reduce((acc, product) => {
        const { available, quantity } = product
        if (available !== false) {
            return acc + quantity * (product.finalPrice || 0)
        }
        return acc
    }, 0)

    return round(total || 0, 2)
}
